import { Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { countriesWithFlagsAndPrefix } from '@/constants/countries-with-flag';
import translationFallback from '@/utilites/translations/translationFallback';
import { PhoneDropdownProps } from '@/types/other';
import loadingStyles from '../contactForm.module.scss';
import SpiritoryInput from './Forms/SpiritoryInput';
import SpiritoryLabel from './Forms/SpiritoryLabel';

export default function PhoneDropdown({
  phoneNumber,
  handlePhoneChange,
  setCurrentPhonePrefixUser,
  loading,
  isValidPhoneNumber = false,
  CustomLabel,
  inputClassName = '',
  currentPhonePrefixUser,
  isOnBoarding,
  disabled = false,
  required = false,
}: PhoneDropdownProps) {
  const { t: tCommon } = useTranslation('common');

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const phonePrefixValue = {
      prefix: e.target.value,
      flag: countriesWithFlagsAndPrefix.find(country => country.phonePrefix === e.target.value)
        ?.flag,
    };
    setCurrentPhonePrefixUser(phonePrefixValue);
  };

  if (isOnBoarding) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-2">
        <SpiritoryLabel htmlFor="phoneNumber" required={required}>
          {translationFallback('phone_number', tCommon('phone_number'), 'Phone Number')}
        </SpiritoryLabel>
        <div className="tw-flex tw-items-stretch">
          <div className="tw-relative tw-flex tw-w-20 tw-items-center">
            <div className="tw-ml-3" aria-live="polite">
              <Image
                src={currentPhonePrefixUser.flag}
                width={23}
                height={23}
                alt=""
                aria-hidden="true"
              />
            </div>
            <Form.Select
              className="!tw-absolute tw-top-0 !tw-h-full !tw-w-full !tw-cursor-pointer !tw-bg-transparent !tw-text-transparent"
              aria-label={`Select country prefix, currently selected: ${currentPhonePrefixUser.prefix}`}
              name="country"
              required={required}
              disabled={disabled || loading}
              onChange={handleSelectChange}
              value={currentPhonePrefixUser.prefix}
            >
              {countriesWithFlagsAndPrefix.map((country, index) => (
                <option className="tw-text-black" key={index} value={country.phonePrefix}>
                  {translationFallback(
                    `allCountries.${country.name.toLowerCase().replace(/\s/g, '_')}`,
                    tCommon(`allCountries.${country.name.toLowerCase().replace(/\s/g, '_')}`),
                    country.name
                  )}{' '}
                  {`(${country.phonePrefix})`}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="tw-relative tw-basis-full">
            <span className="tw-absolute tw-left-2 tw-top-2/4 tw-z-10 -tw-translate-y-2/4">
              {currentPhonePrefixUser.prefix}
            </span>
            <SpiritoryInput
              type="tel"
              name="phoneNumber"
              required={required}
              value={phoneNumber}
              onChange={handlePhoneChange}
              disabled={disabled || loading}
              id="phone-number"
              placeholder={translationFallback(
                'enter_phone_number',
                tCommon('enter_phone_number'),
                'Enter phone number'
              )}
              aria-describedby="phone-prefix-info"
              isPhone={true}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Form.Group className="mb-3">
      {CustomLabel ? (
        CustomLabel
      ) : (
        <Form.Label className="text-secondary fw-semibold font-size-14 mb-3">
          {translationFallback('phone_number', tCommon('phone_number'), 'Phone Number')}
          {required && <span style={{ color: '#FF6347', marginLeft: '2px' }}>*</span>}
        </Form.Label>
      )}

      <div className="tw-flex tw-items-center">
        <div className="tw-relative tw-flex !tw-h-9 tw-w-20 tw-items-center">
          <div className="tw-ml-3" aria-live="polite">
            <Image
              src={currentPhonePrefixUser.flag}
              width={23}
              height={23}
              alt=""
              aria-hidden="true"
            />
          </div>
          <Form.Select
            className="!tw-absolute tw-top-0 !tw-h-full !tw-w-full !tw-cursor-pointer !tw-bg-transparent !tw-text-transparent"
            aria-label={`Select country prefix, currently selected: ${currentPhonePrefixUser.prefix}`}
            name="country"
            required={required}
            disabled={disabled || loading}
            onChange={handleSelectChange}
            value={currentPhonePrefixUser.prefix}
          >
            {countriesWithFlagsAndPrefix.map((country, index) => (
              <option className="tw-text-black" key={index} value={country.phonePrefix}>
                {translationFallback(
                  `allCountries.${country.name.toLowerCase().replace(/\s/g, '_')}`,
                  tCommon(`allCountries.${country.name.toLowerCase().replace(/\s/g, '_')}`),
                  country.name
                )}{' '}
                {`(${country.phonePrefix})`}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="tw-relative tw-basis-full">
          <InputGroup hasValidation className="!tw-relative">
            <span className="tw-absolute tw-left-2 tw-top-2/4 tw-z-10 -tw-translate-y-2/4">
              {currentPhonePrefixUser.prefix}
            </span>
            <Form.Control
              className={`!tw-pl-14 ${loading ? loadingStyles['loading-skeleton'] : ''} ${inputClassName}`}
              type="text"
              name="phoneNumber"
              required={required}
              value={phoneNumber}
              onChange={handlePhoneChange}
              disabled={disabled || loading}
              id="phone-number"
              placeholder={translationFallback(
                'enter_phone_number',
                tCommon('enter_phone_number'),
                'Enter phone number'
              )}
              aria-describedby="phone-prefix-info"
              isValid={isValidPhoneNumber}
            />
          </InputGroup>
        </div>
      </div>
    </Form.Group>
  );
}
